/**
 * The accounts a customer holds — `GET /v1/accounts` (`go-wallet#379`).
 *
 * # Transcribed from the contract, not invented
 *
 * Every type here comes from `api/openapi.yaml` as published by `go-wallet#392`.
 * That distinction is load-bearing in this repo: `WalletApi`'s other read
 * methods were typed against endpoints that do not exist (§9), so they describe
 * a shape somebody hoped for. This one describes a shape the backend answers
 * with, and the fields below carry the contract's own reasoning because most of
 * them are easy to get subtly, invisibly wrong.
 *
 * # The three that a client gets wrong by being helpful
 *
 *   `iban_status`   An account exists BEFORE its IBAN does — the row is created
 *                   on acceptance, the number arrives on a later callback.
 *                   `pending` is normal and is not an error. Branch on this,
 *                   never on whether some string happens to be present.
 *
 *   `can_receive`   Answered by the backend rather than derived from `state`,
 *                   "so a client cannot show a control that always fails". A
 *                   client that computes it from `state` is making an
 *                   authorisation decision in a browser bundle (invariant 8),
 *                   and it would get it wrong immediately: a FROZEN account
 *                   still answers true, because refusing inbound is not ours to
 *                   do.
 *
 *   closed accounts They are listed, and they keep `can_cash_out: true`.
 *                   Filtering them out is what stranded money: cash-out accepts
 *                   no reference other than one from this list, so a closed
 *                   account's remaining balance was reachable by no client at
 *                   all. Never treat presence in the list as permission to
 *                   advertise the account for payment — that is `can_receive`.
 *
 * # And the one this module must never do
 *
 * **Parse anything.** `account_ref` is the institution's opaque reference and an
 * IBAN is an account number. Neither is a structure to read fields out of.
 */

import { isNotACustomer } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

/** `AccountSummary.state`. */
export const ACCOUNT_STATES = ['active', 'restricted', 'frozen', 'closed'] as const
export type AccountState = (typeof ACCOUNT_STATES)[number]

/** `AccountSummary.iban_status`. */
export const IBAN_STATUSES = ['pending', 'issued'] as const
export type IbanStatus = (typeof IBAN_STATUSES)[number]

/**
 * The customer-facing copy for an account's state, from the same table the
 * operator console quotes.
 *
 * `headline` and `detail` are rendered verbatim and `code` is what anything
 * branches on — the same split `onboarding.blocking` already uses, and for the
 * same reason: one wording across the web, iOS and Android, rather than three
 * apps disagreeing about what "restricted" means to the person it happened to.
 */
export interface AccountNotice {
  readonly code: string
  readonly headline: string
  readonly detail: string
  /** Whether this is a state the customer should contact support about. */
  readonly supportPath: boolean
}

export interface Account {
  /**
   * The institution's opaque reference, and the only value
   * `POST /v1/cash-outs` accepts.
   *
   * Never parsed and never displayed as though it meant something. It is
   * already scoped to the authenticated customer — the route has no parameter
   * that could name anybody else.
   */
  readonly accountRef: string
  /** Required, never defaulted (invariant 4). An account with no currency does not exist. */
  readonly currency: string
  readonly state: AccountState
  readonly canReceive: boolean
  readonly canCashOut: boolean
  readonly ibanStatus: IbanStatus
  /** Country prefix and last four, present only when `issued`. Never enough to be paid into. */
  readonly ibanMasked?: string
  /** The backend's sentence for the pending case. Rendered verbatim. */
  readonly ibanNote?: string
  readonly openedAt?: string
  readonly notice: AccountNotice
}

/** `GET /v1/accounts/{account_ref}` — the same account, plus what it takes to be paid. */
export interface AccountDetail extends Account {
  /** Absent — not blank — while `ibanStatus` is `pending`. */
  readonly iban?: string
  readonly bic?: string
  /**
   * The customer's own name AS THE INSTITUTION SET IT, which is the name a
   * paying platform's form has to match. It can differ from the legal name we
   * hold, and theirs is the one that matters.
   */
  readonly holderName?: string
}

/**
 * What the accounts screen knows.
 *
 * `none` and `unavailable` are separate for the reason `#22` made expensive:
 * the contract says an empty list is a normal answer and a `500` is not one,
 * and collapsing them tells a customer they have no account when the truth is
 * that we could not look. That sends them to support about their onboarding.
 *
 * `unregistered` is separate again — signed in, no wallet yet, the state every
 * new customer passes through.
 */
export type AccountsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly accounts: readonly Account[] }
  /** The institution has not accepted this customer, so they hold no account. */
  | { readonly status: 'none' }
  /** Signed in, not a customer yet. */
  | { readonly status: 'unregistered' }
  /** We could not find out. NOT the same as holding none. */
  | { readonly status: 'unavailable'; readonly reason: string }

interface NoticeDTO {
  code?: unknown
  headline?: unknown
  detail?: unknown
  support_path?: unknown
}

interface AccountDTO {
  account_ref?: unknown
  currency?: unknown
  state?: unknown
  can_receive?: unknown
  can_cash_out?: unknown
  iban_status?: unknown
  iban_masked?: unknown
  iban_note?: unknown
  opened_at?: unknown
  notice?: NoticeDTO
  iban?: unknown
  bic?: unknown
  holder_name?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

function isState(v: unknown): v is AccountState {
  return typeof v === 'string' && (ACCOUNT_STATES as readonly string[]).includes(v)
}

function isIbanStatus(v: unknown): v is IbanStatus {
  return typeof v === 'string' && (IBAN_STATUSES as readonly string[]).includes(v)
}

/**
 * Decode one account, or say why not.
 *
 * Defensive rather than cast, and it REFUSES rather than substitutes. A browser
 * bundle can be months older than the backend, so an unknown `state` or
 * `iban_status` will happen — and the helpful responses are both wrong here.
 * Defaulting an unknown state to `active` advertises an account for payment
 * that the institution has restricted; defaulting `iban_status` to `pending`
 * hides an IBAN the customer is waiting for. Saying "this bundle cannot read
 * that" is the only answer that is not a lie about somebody's money.
 */
export function decodeAccount(dto: AccountDTO): Account | string {
  const accountRef = str(dto.account_ref)
  if (accountRef === undefined) return 'an account arrived with no reference'

  // Never defaulted, and not merely "present": a blank currency is the same
  // failure as a missing one (invariant 4).
  //
  // And deliberately NOT checked for membership in money.ts's exponent table,
  // which #93 made a closed set. That asymmetry is a decision, not an oversight
  // someone should tidy up: the membership rule exists to stop an unknown SCALE
  // being guessed at, and an account carries no amount to scale — this surface
  // has no balance at all, so the code is a label and nothing computes with it.
  // Refusing here would trade a label we cannot pretty-print for the customer
  // losing the whole account row, including the IBAN they came to read, on the
  // day the backend opens a currency before this bundle ships one.
  const currency = str(dto.currency)
  if (currency === undefined) return `account ${accountRef} arrived with no currency`

  if (!isState(dto.state)) {
    return `the backend reported an account state this app does not know (${String(dto.state)})`
  }
  if (!isIbanStatus(dto.iban_status)) {
    return `the backend reported an IBAN status this app does not know (${String(dto.iban_status)})`
  }

  const n = dto.notice ?? {}
  const notice: AccountNotice = {
    code: str(n.code) ?? 'unknown',
    headline: str(n.headline) ?? '',
    detail: str(n.detail) ?? '',
    supportPath: n.support_path === true,
  }

  return {
    accountRef,
    currency,
    state: dto.state,
    // `=== true`, not truthiness. A missing flag is false — which for
    // `canReceive` means we do not advertise the account, and that is the safe
    // direction of the two (invariant 8 is fail-closed).
    canReceive: dto.can_receive === true,
    canCashOut: dto.can_cash_out === true,
    ibanStatus: dto.iban_status,
    ...(str(dto.iban_masked) === undefined ? {} : { ibanMasked: str(dto.iban_masked) as string }),
    ...(str(dto.iban_note) === undefined ? {} : { ibanNote: str(dto.iban_note) as string }),
    ...(str(dto.opened_at) === undefined ? {} : { openedAt: str(dto.opened_at) as string }),
    notice,
  }
}

/**
 * Read the customer's accounts.
 *
 * The empty list is `none`, not `ready` with nothing in it, because the screen
 * has something specific and true to say about it.
 */
export async function fetchAccounts(api: WalletApi): Promise<AccountsState> {
  let dto: { accounts?: unknown }
  try {
    dto = await api.get<{ accounts?: unknown }>('/v1/accounts')
  } catch (err) {
    if (isNotACustomer(err)) return { status: 'unregistered' }
    return { status: 'unavailable', reason: err instanceof Error ? err.message : String(err) }
  }

  if (!Array.isArray(dto?.accounts)) {
    return { status: 'unavailable', reason: 'the backend did not return a list of accounts' }
  }

  const accounts: Account[] = []
  for (const raw of dto.accounts as AccountDTO[]) {
    const decoded = decodeAccount(raw ?? {})
    // One unreadable account fails the whole read rather than silently
    // shortening the list. A customer who holds two accounts and is shown one
    // has no way to know, and the one hidden might be the one holding money.
    if (typeof decoded === 'string') return { status: 'unavailable', reason: decoded }
    accounts.push(decoded)
  }

  return accounts.length === 0 ? { status: 'none' } : { status: 'ready', accounts }
}

/**
 * Read one account in full — `GET /v1/accounts/{account_ref}`.
 *
 * Separate from the list on purpose, and called only when the customer asks.
 * The list deliberately does not carry the number so that polling for issuance
 * does not ship an account number on every tick; fetching it here on every list
 * render would hand that property straight back.
 */
export async function fetchAccountDetail(
  api: WalletApi,
  accountRef: string,
): Promise<AccountDetail | string> {
  let dto: AccountDTO
  try {
    // Encoded, because the reference is opaque: this app does not get to assume
    // which characters it contains.
    dto = await api.get<AccountDTO>(`/v1/accounts/${encodeURIComponent(accountRef)}`)
  } catch (err) {
    return err instanceof Error ? err.message : String(err)
  }

  // `?? {}` for parity with the list path, and it is not defensive padding: a
  // body of literal `null` is valid JSON that a backend can emit, and
  // `decodeAccount` reads `dto.account_ref` immediately. Without it this throws
  // out of a function whose signature promises `AccountDetail | string` — a
  // type that lies, and the caller wedged on it (#72).
  const base = decodeAccount(dto ?? {})
  if (typeof base === 'string') return base

  return {
    ...base,
    ...(str(dto.iban) === undefined ? {} : { iban: str(dto.iban) as string }),
    ...(str(dto.bic) === undefined ? {} : { bic: str(dto.bic) as string }),
    ...(str(dto.holder_name) === undefined ? {} : { holderName: str(dto.holder_name) as string }),
  }
}

/** Human labels. Ours — the backend's `notice` is what speaks about a specific account. */
export const STATE_LABEL: Record<AccountState, string> = {
  active: 'Open',
  restricted: 'Restricted',
  frozen: 'Frozen',
  closed: 'Closed',
}
